import { z } from "zod";

export const TrackingType = z.enum([
	"injection",
	"surgery",
	"medication",
	"product",
	"shockwave",
	"education",
]);

export type TrackingType = z.infer<typeof TrackingType>;

export const QuickstartTrackerT = z.object({
	form_id: z.string(),
	schedule_id: z.string(),
});

export type QuickstartTrackerT = z.infer<typeof QuickstartTrackerT>;

export const QuickstartInjectionDetailT = z.object({
	side: z.string().nullable(),
	orthopaedic_area: z.string().nullable(),
	site: z.string().nullable(),
	injection_subsite: z.string().nullable(),
	injection_substance: z.string().nullable(),
	dose: z.string().nullable(),
	unit: z.string().nullable(),
});

export type QuickstartInjectionDetailT = z.infer<
	typeof QuickstartInjectionDetailT
>;

export const QuickstartShockwaveDetailT = z.object({
	side: z.string().nullable(),
	orthopaedic_area: z.string().nullable(),
	site: z.string().nullable(),
	subsite: z.string().nullable(),
	shockwave_type: z.string().nullable(),
	dose: z.string().nullable(),
	unit: z.string().nullable(),
	impulses: z.string().nullable(),
});

export type QuickstartShockwaveDetailT = z.infer<
	typeof QuickstartShockwaveDetailT
>;

export interface QuickstartInjectionEventT {
	type: "injection";
	referring_clinician?: string;
	injection_clinician?: string;
	injection_method?: string;
	side?: string;
	anatomic_area?: string;
	site?: string;
	injection_subsite?: string;
	substance?: string;
	dose?: string;
	unit?: string;
}

export interface QuickstartSurgeryEventT {
	type: "surgery";
	referring_clinician?: string;
	surgery_type?: string;
	surgery_details?: string;
	side?: string;
	anatomic_area?: string;
}

export interface QuickstartMedicationEventT {
	type: "medication";
	medication_name?: string;
	dose?: string;
	unit?: string;
	frequency?: string;
	duration?: string;
}

export interface QuickstartProductEventT {
	type: "product";
	product_name?: string;
	product_type?: string;
	usage_instructions?: string;
}

export interface QuickstartShockwaveEventT {
	type: "shockwave";
	referring_clinician?: string;
	treatment_area?: string;
	intensity?: string;
	duration?: string;
}

export type QuickstartEventT =
	| QuickstartInjectionEventT
	| QuickstartSurgeryEventT
	| QuickstartMedicationEventT
	| QuickstartProductEventT
	| QuickstartShockwaveEventT;

export const ContactSchema = z.object({
	contact_id: z.string().uuid("Contact person is required"),
});

const injectionDetailSchema = z.object({
	injection_detail_id: z.string().uuid().optional(),
	injection_id: z.string().uuid().optional(),
	side: z.string().nullable().optional(),
	orthopaedic_area: z.string().nullable().optional(),
	site: z.string().nullable().optional(),
	subsite: z.string().nullable().optional(),
	injection_substance: z.string().nullable().optional(),
	dose: z.string().nullable().optional(),
	unit: z.string().nullable().optional(),
});

const shockwaveDetailSchema = z.object({
	shockwave_detail_id: z.string().uuid().optional(),
	shockwave_id: z.string().uuid().optional(),
	side: z.string().nullable().optional(),
	orthopaedic_area: z.string().nullable().optional(),
	site: z.string().nullable().optional(),
	shockwave_type: z.enum(["radial", "focused"]).nullable().optional(),
	dose: z.string().nullable().optional(),
	unit: z.string().optional(),
	impulses: z.string().nullable().optional(),
});

export const templateSettingsSchema = z.object({
	// Quickstart
	quickstart_key: z.string(),

	// Admin Form
	organisation_id: z.string(),
	organisation_name: z.string(),
	contact_id: ContactSchema.shape.contact_id,
	name: z.string(),
	description: z.string(),
	invited_by: z.string().optional(),
	invited_by_formal_name: z.string().optional(),

	// Trackers Form
	trackers: z.array(QuickstartTrackerT),

	// Events Form
	required_events: z.array(TrackingType),

	// Injection Form
	injection_id: z.string().optional(),
	referring_clinician_id: z.string().optional(),
	injecting_clinician_id: z.string().optional(),
	injection_method_id: z.string().optional(),
	injection_details: z.array(injectionDetailSchema).optional(),

	// Surgery Form
	surgery_id: z.string(),
	operating_clinician_id: z.string().nullable().optional(),
	side: z.string().nullable().optional(),
	orthopaedic_area: z.string().nullable().optional(),
	surgery_option_id: z.string().nullable().optional(),

	// Education Form
	education_id: z.string().optional(),
	education_name: z.string().optional(),
	education_description: z.string().nullable().optional(),
	educated_by: z.string().nullable().optional(),

	// Shockwave Form
	shockwave_id: z.string().optional(),
	shockwaving_clinician_id: z.string().optional(),
	shockwave_details: z.array(shockwaveDetailSchema).optional(),
});

export type TemplateSettingsSchemaT = z.infer<typeof templateSettingsSchema>;

export type QuickstartDataSchemaT = TemplateSettingsSchemaT & {
	patient_id: string;
};

export interface QuickstartSettingsStepT {
	stage: "past" | "current" | "future";
	words: string;
}
